import { useEffect, useRef, useState } from 'react';
import { feedbackAppVersion, FEEDBACK_COMMENT_MAX, FEEDBACK_FAILED_MESSAGE, FEEDBACK_LIMIT_MESSAGE } from '../models/feedback';
import { api } from '../services/api';
import { ApiException } from '../services/apiClient';
import { Logger } from '../services/logger';
import { useFeedback } from '../state/feedback';
import { useShell } from '../state/shell';
import { Sheet } from './Sheet';

const log = new Logger('FeedbackSheet');

/** Slides up at the 10 and 60 minute marks and when the player taps Send feedback on the You tab. However it closes it does not reopen; a send that fails is logged and the player is told. */
export function FeedbackSheet() {
  const isOpen = useFeedback((s) => s.isOpen);
  const close = useFeedback((s) => s.close);
  if (!isOpen) return null;
  return <FeedbackForm onClose={close} />;
}

function FeedbackForm({ onClose }: { onClose: () => void }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const canSend = rating > 0 || comment.trim() !== '';
  const root = useRef<HTMLDivElement>(null);

  // Browsers that leave the layout viewport alone when the keyboard opens (iOS Safari) would hide the
  // comment box and Send button behind it, so pad the panel by the part of the window the keyboard covers.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const fit = () => {
      const covered = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.current?.style.setProperty('--keyboard', `${covered}px`);
    };
    fit();
    vv.addEventListener('resize', fit);
    vv.addEventListener('scroll', fit);
    return () => {
      vv.removeEventListener('resize', fit);
      vv.removeEventListener('scroll', fit);
    };
  }, []);

  const send = () => {
    const text = comment.trim();
    onClose();
    api
      .submitFeedback({ rating: rating > 0 ? rating : null, comment: text === '' ? null : text, platform: 'web', appVersion: feedbackAppVersion(__APP_VERSION__) })
      .then(() => useShell.getState().showToast('Thanks for the feedback!'))
      .catch((error: unknown) => {
        log.warn('feedback not sent', { error });
        if (error instanceof ApiException && error.isFeedbackLimit) useShell.getState().showToast(FEEDBACK_LIMIT_MESSAGE, { durationMs: 7000 });
        else useShell.getState().showToast(FEEDBACK_FAILED_MESSAGE);
      });
  };

  return (
    <Sheet label="How is EthrHunt going?" onClose={onClose}>
      <div ref={root} className="feedback-sheet" data-testid="feedback-sheet">
        <h2 className="t-title-l feedback-title">How is EthrHunt going?</h2>
        <div className="feedback-stars" role="group" aria-label="Rating">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              className={`feedback-star${n <= rating ? ' on' : ''}`}
              aria-label={`${n} star${n === 1 ? '' : 's'}`}
              aria-pressed={n <= rating}
              data-testid={`feedback-star-${n}`}
              onClick={() => setRating(n)}
            >
              {n <= rating ? '★' : '☆'}
            </button>
          ))}
        </div>
        <textarea
          className="input feedback-comment"
          data-testid="feedback-comment"
          aria-label="Comment"
          rows={3}
          maxLength={FEEDBACK_COMMENT_MAX}
          placeholder="Anything we should know? (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onFocus={(e) => e.currentTarget.scrollIntoView?.({ block: 'nearest' })}
        />
        <div className="feedback-actions">
          <button type="button" className="btn text" data-testid="feedback-not-now" onClick={onClose}>
            Not now
          </button>
          <button type="button" className="btn filled" data-testid="feedback-send" disabled={!canSend} onClick={send}>
            Send
          </button>
        </div>
      </div>
    </Sheet>
  );
}
